/** @jsx jsx */
import { routeTo } from 'redux-router-kit';
import { useState, Fragment } from 'react';

import { Margin } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import { GET_HELP_PATH } from 'app/developer-v3/teams/team-technical-support-operations/get-help';
import { GUIDE_SECTION_IDS } from 'app/developer-v3/components/SidebarRight/constants';
import { ValidationResultSetWrapper } from 'app/developer-v3/types';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { getAppUrl } from 'app/developer-v3/utils';
import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import {
  TABS as SIDEBAR_RIGHT_TABS,
  MODES as SIDEBAR_RIGHT_MODES,
  useSidebarRight,
} from 'app/developer-v3/components/SidebarRight';
import {
  CloneModal,
  CloneFinishedModal,
} from 'app/developer-v3/components/VersionModals';

import CtaCloneSuccess from './CtaCloneSuccess';
import CtaNonEditableVersion from './CtaNonEditableVersion';

import type { SemVer } from 'app/developer-v3/types/version';

type State = {
  isModalOpen: boolean;
  toVersion: SemVer;
};

type Props = {
  appId: string;
  appTitle: string;
  appVersion: SemVer;
  currentBuildUrl: string;
  isLifecycleRequestFailed: boolean;
  isLifecycleRequestStarted: boolean;
  isLifecycleRequestSuccessful: boolean;
  lifecycleErrorMessage: string | ValidationResultSetWrapper;
  onClone: (appId: string, fromVersion: SemVer, toVersion: SemVer) => void;
  userCount: number;
  versions: Array<{ isGUIIntegration?: boolean; version: SemVer }>;
  viewAppVersion: (toVersion: SemVer, path?: string) => void;
};

/**
 * Renders a CTA warning the user about a non editable version of the integration.
 *
 * The CTA has a clone button that allows the user to clone the integration
 * from the current page.
 * Once cloning has been successfully, we render a success CTA.
 *
 * Both CTAs also have a button to the versioning guide.
 * This button opens the right sidebar in the guide section.
 *
 * Once the clone request has finished we render the result
 * and allow the user to edit the version both through the Modal or
 * through the CTA
 *
 */
const NonEditableUI = ({
  appId,
  appTitle,
  appVersion,
  currentBuildUrl,
  isLifecycleRequestFailed,
  isLifecycleRequestStarted,
  isLifecycleRequestSuccessful,
  lifecycleErrorMessage,
  onClone,
  userCount,
  versions,
  viewAppVersion,
}: Props) => {
  const { update: updateSidebarRight } = useSidebarRight();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isProduction =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.PRODUCTION;
  const isUserCountLimitExceeded =
    currentVersionReadOnlyReason ===
    CurrentVersionReadOnlyReason.USER_COUNT_LIMIT_EXCEEDED;

  const openVersionHelp = () =>
    updateSidebarRight({
      mode: SIDEBAR_RIGHT_MODES.DEFAULT,
      tabActive: SIDEBAR_RIGHT_TABS.GUIDE,
      activeSubsection: GUIDE_SECTION_IDS.VERSIONS,
    });

  const [nonEditableUiStatus, setNonEditableUiStatus] = useState<State>({
    isModalOpen: false,
    toVersion: '',
  });

  const updateNonEditableUiStatus = properties => {
    setNonEditableUiStatus({
      ...nonEditableUiStatus,
      ...properties,
    });
  };

  const closeModal = () =>
    updateNonEditableUiStatus({
      isModalOpen: false,
    });

  const isLifecycleRequestFinished =
    isLifecycleRequestSuccessful || isLifecycleRequestFailed;
  const waitingOnRequest =
    isLifecycleRequestStarted && !isLifecycleRequestFinished;

  return (
    <Fragment>
      {isLifecycleRequestSuccessful && !isLifecycleRequestFailed ? (
        <Margin margin="20px 0px 20px 0px">
          <CtaCloneSuccess
            onEditClick={() =>
              viewAppVersion(nonEditableUiStatus.toVersion, currentBuildUrl)
            }
            openVersionHelp={openVersionHelp}
            versionsLink={`${getAppUrl(appId)}/versions`}
          />
        </Margin>
      ) : (
        (isProduction || isUserCountLimitExceeded) && (
          <Margin margin="20px 0px 20px 0px">
            <CtaNonEditableVersion
              appName={appTitle}
              onCloneClick={() =>
                updateNonEditableUiStatus({
                  isModalOpen: true,
                })
              }
              openVersionHelp={openVersionHelp}
              userCount={userCount}
            />
          </Margin>
        )
      )}
      {nonEditableUiStatus.isModalOpen &&
        (isLifecycleRequestFinished ? (
          <CloneFinishedModal
            errors={lifecycleErrorMessage}
            isFailure={isLifecycleRequestFailed}
            isSuccess={isLifecycleRequestSuccessful}
            onClose={closeModal}
            onConfirm={() => {
              closeModal();
              if (isLifecycleRequestSuccessful) {
                viewAppVersion(nonEditableUiStatus.toVersion, currentBuildUrl);
              } else {
                routeTo(`${ZAPIER_APP_BASE_URL}${GET_HELP_PATH}`);
              }
            }}
            toVersion={nonEditableUiStatus.toVersion}
          />
        ) : (
          <CloneModal
            fromVersion={appVersion}
            isSaving={waitingOnRequest}
            onClose={closeModal}
            onConfirm={() =>
              onClone(appId, appVersion, nonEditableUiStatus.toVersion)
            }
            onSetToVersion={option =>
              updateNonEditableUiStatus({ toVersion: option.value })
            }
            toVersion={nonEditableUiStatus.toVersion}
            versions={versions}
          />
        ))}
    </Fragment>
  );
};

export default NonEditableUI;
